import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
  Loader,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { CommercialScheduleServices } from "../../../core/services/CommercialScheduleServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import {
  CommercialFields,
  CommercialListParams,
} from "../Commercials/CommercialDataTypes";
import { Permission } from "../../../core/types/PermissionInterface";
import "./CommercialSchedules.css";
import CommercialFilters from "../Commercials/CommercialFilters";
import {
  isEmptyVal,
  displayField,
  displayDateField,
  getTodayShortDay,
} from "../../../utils/helpers";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import useDecryptedParams from "../../../navigation/useDecryptParams";
import {
  statusArr,
  messageTypeArr,
  appConstObj,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { CommercialServices } from "../../../core/services/CommercialServices";
import MediaPlayerFormatter from "../../components/Elements/MediaPlayerFormatter";
import AddItemsToCommercialScheduleBulkAction from "./AddItemsToCommercialScheduleBulkAction";
import { setSearchString } from "../../../redux/user";
import CommercialInfoCell from "../../components/Elements/CommercialInfoCell";
import Company from "../Companies/Company";
import CommercialScheduleItems from "./CommercialScheduleItems";
import AudioPlayer from "../../components/Elements/AudioPlayer";
import { useMediaContext } from "../../../context/MediaContext";

function AddItemsToCommercialSchedule() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [reloadKey, setReloadKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [totalItems, setTotalItems] = useState(null);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CommercialFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const [commercialOfComp, setCommercialOfComp] = useState<any>("");
  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const [selectedTime, setSelectedTime] = useState<string>("");
  const [selectedDay, setSelectedDay] = useState<string>("");
  const [onExactTime, setOnExactTime] = useState<string>("");
  const params: any = useParams();
  let [decryptedParams] = useDecryptedParams(params);
  const [commercialScheduleLoader, setCommercialScheduleLoader] =
    useState(false);
  const [commercialScheduleData, setCommercialScheduleData] = useState<any>({});
  const { playingMediaInfo, setPlayingMediaInfo, isPlaying, setIsPlaying } =
    useMediaContext();
  const [itemSearchParams, setItemSearchParams] = useState<any>({
    smart_search: "",
    play_for_day: getTodayShortDay().toLowerCase(),
    start_hour: "",
  });

  function submitSearchAfterItemChange(
    prevCommercialScheduleId: any,
    itmSearchParams?: any
  ) {
    setPage(page);
    getItemsForCommercialSchedule(getSearchParams(formData));
    itmSearchParams && setItemSearchParams(itmSearchParams);
    setReloadKey((prevKey) => prevKey + 1);
  }

  const initSearchFormData = {
    name: "",
    media_type: "",
    sourced_from: "",
    media_nature: "",
    commercial_use_for: "",
    company: "",
    brand: "",
    campaign: "",
    status: "",
    smart_search: "",
  };

  const [formData, setFormData] =
    useState<CommercialListParams>(initSearchFormData);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = React.useState<number[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };
  function getCommercialScheduleId() {
    return decryptedParams.id ? decryptedParams.id.toString() : null;
  }
  async function getCommercialScheduleDetail(id: number) {
    try {
      setCommercialScheduleLoader(true);
      const res = await CommercialScheduleServices.viewCommercialSchedule(id);
      const respData = get("data.data", res);
      setCommercialScheduleData({ ...respData });
      setCommercialOfComp(respData?.company_id);
      setTotalItems(respData?.total_items);
      getItemsForCommercialSchedule(
        getSearchParams({
          company: respData.company_id,
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        })
      );
      setCommercialScheduleLoader(false);
    } catch (error) {
      setCommercialScheduleLoader(false);
    }
  }
  async function getItemsForCommercialSchedule(params: CommercialListParams) {
    try {
      setLoading(true);
      const res = await CommercialServices.getCommercials(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
      navigate(ContentRoutes.COMMERCIAL_SCHEDULES);
    }
  }

  async function bulkAddAction() {
    try {
      let params = {
        media_id: checkedKeys.join(),
        play_starts_at: selectedTime,
        play_for_day: selectedDay,
        play_on_exact_time: onExactTime,
      };
      let res = await CommercialScheduleServices.addItemsToCommercialSchedule(
        commercialScheduleData.id,
        params
      );
      UtilityServices.showToaster({
        message: res?.data?.message,
      });
      setTotalItems(res?.data?.data?.total_items);
      getItemsForCommercialSchedule(
        getSearchParams({
          smart_search: smartSearchInput,
        })
      );
      setReloadKey((prevKey) => prevKey + 1);
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function init() {
    try {
      getCommercialScheduleId() &&
        getCommercialScheduleDetail(getCommercialScheduleId());
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, [decryptedParams.id]);

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ exclude_start_time: selectedTime },
      ...{ exclude_play_for_day: selectedDay },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: CommercialListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    if (commercialOfComp > 0) {
      params.company = commercialOfComp;
    }
    if (getCommercialScheduleId() > 0) {
      params.exclude_items_from_schedule = getCommercialScheduleId();
    }

    return params;
  }

  function onPageChange(page: number) {
    setPage(page);
    getItemsForCommercialSchedule(
      getSearchParams({
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getItemsForCommercialSchedule(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getItemsForCommercialSchedule(getSearchParams(formData));
  }

  function submitExcludedSearch(params: any) {
    return;
    getItemsForCommercialSchedule(getSearchParams(params));
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getItemsForCommercialSchedule(
      getSearchParams({
        ...initSearchFormData,
        ...{
          page: 1,
          rows: appConstObj.record_per_page,
        },
      })
    );
  }

  const filterItems = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getItemsForCommercialSchedule(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getItemsForCommercialSchedule(
      getSearchParams({
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      })
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getItemsForCommercialSchedule(
      getSearchParams({
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      })
    );
  };
  return (
    <div className="add-items-to-commercial-schedule">
      {commercialScheduleLoader && (
        <Loader
          style={{ zIndex: 9999 }}
          size="lg"
          content={t("LOADING")}
          center
          backdrop={true}
        />
      )}
      {getCommercialScheduleId() > 0 ? (
        <div className="followups">
          <Container className="container">
            <Header className="header mb-2 add-items-commercial-summary">
              <FlexboxGrid justify="space-between">
                <FlexboxGrid.Item colspan={16}>
                  <h3>
                    <span className="add-items-commercial-summary-label">
                      {t("SCHEDULE")} {t("NAME")}
                    </span>
                    <span>{commercialScheduleData?.schedule_title}</span>
                  </h3>
                  <h4>
                    <span>
                      <b>{t("ITEMS")}</b>: {totalItems}
                    </span>
                    <span>
                      <b>
                        {t("MAX")} {t("ITEMS")}
                      </b>
                      : {commercialScheduleData?.max_items}
                    </span>
                    <span>
                      <b>{t("DATE")}</b>:{" "}
                      {displayDateField(
                        commercialScheduleData?.schedule_date_from
                      )}{" "}
                      {t("TO")}{" "}
                      {displayDateField(commercialScheduleData?.schedule_date_to)}
                    </span>
                  </h4>
                </FlexboxGrid.Item>
              </FlexboxGrid>
            </Header>
            <Content className="add-items-commercial-content">
              <CommercialFilters
                onSubmit={submitSearch}
                onClear={clearSearch}
                showBackToSchedule={true}
              />
              <div className="add-items-commercial-split-layout">
                <div
                  className={
                    total > 0
                      ? "add-items-commercial-list-panel usr-tbl-border"
                      : "add-items-commercial-list-panel no-record-table"
                  }
                >
                  <div className="add-items-commercial-actions-row">
                    <AddItemsToCommercialScheduleBulkAction
                      commercialScheduleData={commercialScheduleData}
                      checkedKeys={checkedKeys}
                      setCheckedKeys={setCheckedKeys}
                      selectedTime={selectedTime}
                      setSelectedTime={setSelectedTime}
                      selectedDay={selectedDay}
                      setSelectedDay={setSelectedDay}
                      onExactTime={onExactTime}
                      setOnExactTime={setOnExactTime}
                      onAddItems={bulkAddAction}
                      onSearchItems={submitExcludedSearch}
                    />
                    <div className="add-items-commercial-search">
                      <InputGroup className="add-items-commercial-smart-search">
                        <Input
                          className="form-control"
                          placeholder={t("SMART_SEARCH_BY", {
                            entity: t("TITLE").toLowerCase(),
                          })}
                          value={smartSearchInput}
                          onChange={(val: any) => setSmartSearch(val)}
                          onKeyDown={(e: any) => filterItems(e)}
                        />
                        <InputGroup.Button
                          title={t("SEARCH")}
                          onClick={(e: any) => filterItems(e)}
                        >
                          <SearchIcon />
                        </InputGroup.Button>
                        <InputGroup.Button
                          title={t("CLEAR")}
                          onClick={() => clearSmartSearch()}
                        >
                          <CloseIcon />
                        </InputGroup.Button>
                      </InputGroup>
                    </div>
                  </div>
                  <div
                    className={
                      total > 0 ? "usr-tbl-border mt-2" : "no-record-table mt-2"
                    }
                  >
                    <Table
                      height={400}
                      fillHeight={true}
                      data={data}
                      loading={loading}
                      onSortColumn={onSortChange}
                      sortColumn={sort_by}
                      bordered={true}
                      cellBordered
                      sortType={sort_order}
                      hover={true}
                      renderEmpty={() => (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                          <h4>
                            {t("NO_ENTITY_FOUND_FOR", {
                              entity: t("COMMERCIAL"),
                              for: commercialScheduleData?.chart_title,
                            })}
                          </h4>
                        </div>
                      )}
                    >
                      <Column width={40} align="center" fixed>
                        <CustomHeaderCell>
                          <div></div>
                        </CustomHeaderCell>
                        <CheckCell
                          dataKey="id"
                          checkedKeys={checkedKeys}
                          onChange={handleCheck}
                        />
                      </Column>
                      <Column width={60} align="center" fixed>
                        <CustomHeaderCell>{t("PLAY")}</CustomHeaderCell>
                        <MediaPlayerFormatter
                          rowData={(rowData: any) => rowData}
                          mediaUrlKey={"media_url"}
                          mediaImageKey={"media_image"}
                        />
                      </Column>
                      <Column width={100} sortable resizable flexGrow={1}>
                        <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                        <CommercialInfoCell
                          rowData={(rowData: any) => rowData}
                          dataKey="title"
                          showData={[
                            "campaign_name",
                            "brand_name",
                            "commercial_use_for",
                            "media_duration",
                          ]}
                        />
                      </Column>
                      <Column width={80} sortable resizable>
                        <CustomHeaderCell>{t("TYPE")}</CustomHeaderCell>
                        <CustomCell dataKey="media_nature">
                          {(rowData: any) => (
                            <span
                              title={
                                rowData?.commercial_use_for +
                                " " +
                                rowData.media_nature
                              }
                            >
                              {displayField(rowData.media_nature)} (
                              {rowData.commercial_use_for == "external"
                                ? t("Ext")
                                : t("Int")}
                              )
                            </span>
                          )}
                        </CustomCell>
                      </Column>
                    </Table>
                  </div>
                  <Footer className="footer">
                    <Pagination
                      prev
                      next
                      first
                      last
                      ellipsis
                      boundaryLinks
                      maxButtons={5}
                      size="xs"
                      layout={["total", "-", "limit", "|", "pager", "skip"]}
                      total={total}
                      limitOptions={appConstObj.limit_options}
                      limit={rows}
                      activePage={page}
                      onChangePage={onPageChange}
                      onChangeLimit={handleChangeRows}
                    />
                  </Footer>
                </div>
                <div className="add-items-commercial-separator" aria-hidden="true" />
                <div className="add-items-commercial-schedule-items-panel">
                  <CommercialScheduleItems
                    key={reloadKey}
                    commercialScheduleId={getCommercialScheduleId()}
                    onDeleteItems={submitSearchAfterItemChange}
                    itemSearchParams={itemSearchParams}
                    enableBulkAction={["delete"]}
                    onSetTotalItems={setTotalItems}
                  />
                </div>
                <AudioPlayer />
                <ToastContainer />
              </div>
            </Content>
          </Container>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
}

export default AddItemsToCommercialSchedule;
